import { useEffect, useState } from 'react';
import useEForm from '@/hooks/use-eform/index';
import dataElementsApi from '@/services/api/dataElements.js';

/**
 * Loads the JSON schema for a data element template and seeds the EForm payload.
 * If `existingPayload` is provided (edit/detail flows) the payload is built from
 * it; otherwise the payload is seeded from the schema's default shape.
 *
 * Returns the EForm payload state plus the loaded schema:
 *   { schema, payload, setPayload, isDirty, setIsDirty }.
 *
 * The effect re-runs when `dataElementId` or `existingPayload` change, so callers
 * should pass stable references (state values, not freshly-allocated objects).
 */
const useSchemaForDataElement = ({ dataElementId, existingPayload }) => {
    const {
        payload,
        setPayload,
        isDirty,
        setIsDirty,
        getFullJson,
        extractCombinedSchema,
        getSchemaWithoutAttachment,
    } = useEForm();

    const [schema, setSchema] = useState(null);

    useEffect(() => {
        if (!dataElementId) return;
        dataElementsApi.getDataElementTemplate(dataElementId).then(([error, response]) => {
            if (error || !response?.elementSchema) return;
            const elementSchema = response.elementSchema;
            const plainSchema = extractCombinedSchema(elementSchema, existingPayload);
            const schemaWithoutAttachment = getSchemaWithoutAttachment(plainSchema);
            setSchema(schemaWithoutAttachment);
            setPayload(getFullJson(schemaWithoutAttachment, existingPayload, plainSchema));
        });
    }, [dataElementId, existingPayload]);

    return { schema, payload, setPayload, isDirty, setIsDirty };
};

export default useSchemaForDataElement;
